import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Star, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type UseCase =
  | "call"
  | "work"
  | "study"
  | "break"
  | "interview"
  | "recording"
  | "other";

interface Answers {
  email: string;
  use_case?: UseCase;
  use_case_other?: string;
  followup?: Record<string, unknown>;
  pod_quality?: {
    sound: number;
    cleanliness: number;
    comfort: number;
    lighting: number;
    unlock: number;
  };
  booking_easy?: number;
  price_feel?: string;
  price_note?: string;
  would_book_again?: "yes" | "maybe" | "no";
  would_book_again_why?: string;
  one_change?: string;
}

const useCaseOptions: { value: UseCase; label: string }[] = [
  { value: "call", label: "phone or video call" },
  { value: "work", label: "focused work session" },
  { value: "study", label: "study session" },
  { value: "break", label: "quick break / decompress" },
  { value: "interview", label: "had an interview" },
  { value: "recording", label: "recording (audio or video)" },
  { value: "other", label: "something else" },
];

const Stars = ({ value, onChange, size = 36 }: { value?: number; onChange: (n: number) => void; size?: number }) => (
  <div className="flex gap-2 justify-center">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className="transition-transform hover:scale-110 active:scale-95"
        aria-label={`${n} stars`}
      >
        <Star
          size={size}
          className={n <= (value ?? 0) ? "fill-primary text-primary" : "text-primary/30"}
        />
      </button>
    ))}
  </div>
);

const Pill = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2.5 rounded-full border transition-all text-sm ${
      active
        ? "bg-primary text-primary-foreground border-primary"
        : "bg-card border-border text-foreground hover:border-primary/40"
    }`}
  >
    {children}
  </button>
);

export default function Feedback() {
  const [params] = useSearchParams();
  const uid = params.get("uid");

  const [answers, setAnswers] = useState<Answers>({ email: "" });
  const [step, setStep] = useState(0);
  const [emailLocked, setEmailLocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loadingSession, setLoadingSession] = useState(!!uid);

  // Prefill email from session uid
  useEffect(() => {
    if (!uid) return;
    (async () => {
      const { data } = await supabase
        .from("pod_sessions")
        .select("user_email")
        .eq("id", uid)
        .maybeSingle();
      if (data?.user_email) {
        setAnswers((a) => ({ ...a, email: data.user_email as string }));
        setEmailLocked(true);
      }
      setLoadingSession(false);
    })();
  }, [uid]);

  const update = (patch: Partial<Answers>) => setAnswers((a) => ({ ...a, ...patch }));
  const updateFollowup = (patch: Record<string, unknown>) =>
    setAnswers((a) => ({ ...a, followup: { ...(a.followup ?? {}), ...patch } }));

  // Build dynamic step list
  const steps = useMemo(() => {
    const list: string[] = [];
    if (!emailLocked) list.push("welcome", "email");
    list.push("use_case");
    if (answers.use_case && answers.use_case !== "other") list.push("followup");
    list.push("pod_quality", "booking_price", "again", "one_change");
    return list;
  }, [emailLocked, answers.use_case]);

  const currentKey = steps[step];
  const progress = ((step + 1) / steps.length) * 100;

  const canNext = (): boolean => {
    switch (currentKey) {
      case "email":
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answers.email.trim());
      case "use_case":
        return !!answers.use_case && (answers.use_case !== "other" || !!answers.use_case_other?.trim());
      case "followup":
        return true; // optional
      case "pod_quality":
        return !!answers.pod_quality && Object.values(answers.pod_quality).every((v) => v > 0);
      case "booking_price":
        return !!answers.booking_easy && !!answers.price_feel;
      case "again":
        return !!answers.would_book_again;
      default:
        return true;
    }
  };

  const handleNext = async () => {
    if (step < steps.length - 1) {
      setStep((s) => s + 1);
      return;
    }
    // Submit
    setSubmitting(true);
    setError("");
    try {
      const overall =
        answers.pod_quality
          ? Math.round(
              (answers.pod_quality.sound +
                answers.pod_quality.cleanliness +
                answers.pod_quality.comfort +
                answers.pod_quality.lighting +
                answers.pod_quality.unlock) /
                5,
            )
          : null;

      const { error: insErr } = await supabase.from("feedback_responses").insert({
        session_id: uid || null,
        email: answers.email.trim(),
        use_case: answers.use_case ?? null,
        overall_rating: overall,
        would_book_again: answers.would_book_again ?? null,
        responses: answers as never,
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
      });
      if (insErr) throw insErr;
      setDone(true);
    } catch (e: any) {
      setError("Something went wrong - please try again or message us on WhatsApp.");
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingSession) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground font-sans">loading...</div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* Progress bar */}
        <div className="w-full h-1 bg-muted">
          <motion.div className="h-full bg-primary" style={{ width: "100%" }} />
        </div>

        {/* Toolbar */}
        <header className="px-6 py-6 border-b border-black/5 bg-background">
          <div className="w-full flex items-center justify-between">
            <a href="/"><span className="font-funnel font-bold text-3xl text-foreground">qwip</span></a>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-md text-center w-full"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
              <Check className="text-primary" size={32} />
            </div>
            <h1 className="font-funnel text-4xl text-foreground mb-3">thank you, truly.</h1>
            <p className="text-muted-foreground font-sans mb-8">
              every word helps us build qwip into something you'll keep coming back to.
            </p>

            <div className="bg-card border border-border rounded-2xl p-6 mb-8 text-left">
              <div className="text-xs text-muted-foreground font-sans uppercase tracking-wide mb-2 text-center">
                a small thank-you
              </div>
              <div className="font-funnel text-3xl text-primary text-center mb-2 tracking-wider">QWIP15</div>
              <p className="text-sm text-muted-foreground font-sans text-center">
                15 minutes free on your next session.<br />enter it at checkout.
              </p>
            </div>

            <a
              href="https://wa.me/447347474059"
              className="inline-flex items-center gap-2 text-primary font-sans text-sm hover:underline"
            >
              <MessageCircle size={16} /> have more to say? message us
            </a>
            <div className="mt-8">
              <a href="https://qwip.co.uk" className="text-muted-foreground text-sm font-sans hover:text-foreground">
                ← back to qwip
              </a>
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <footer className="px-6 py-8 border-t border-border bg-background">
          <div className="max-w-xl mx-auto flex flex-col items-center gap-3 text-sm font-sans text-muted-foreground">
            <a href="https://instagram.com/qwip" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
              Follow us on Instagram
            </a>
            <a href="https://apps.apple.com/gb/app/qwip/id1234567890" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
              Download qwip on the App Store
            </a>
          </div>
        </footer>
      </div>
    );
  }


  const isWelcome = currentKey === "welcome";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Progress bar */}
      <div className="w-full h-1 bg-muted">
        <motion.div
          className="h-full bg-primary"
          initial={false}
          animate={{ width: isWelcome ? "0%" : `${progress}%` }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>

      {/* Toolbar (matches home navbar, no Become a Partner) */}
      <header className="px-6 py-6 border-b border-black/5 bg-background">
        <div className="w-full flex items-center justify-between">
          <a href="/">
            <span className="font-funnel font-bold text-3xl text-foreground">qwip</span>
          </a>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentKey}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              {isWelcome ? (
                <div className="text-center max-w-md mx-auto -mt-8">
                  <h2 className="font-funnel font-semibold text-2xl text-foreground mb-3">
                    We'd love to hear how you found your qwip session.
                  </h2>
                  <div className="text-muted-foreground font-sans leading-relaxed mb-10">
                    <p className="mb-4">
                      Your feedback helps us make the experience smoother for you and the next customer.
                    </p>
                    <p>It takes less than a minute, and there's a small gift at the end 👀</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep((s) => s + 1)}
                    className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-8 py-3.5 rounded-full font-sans text-sm transition-opacity hover:opacity-90"
                  >
                    start <ArrowRight size={16} />
                  </button>
                </div>
              ) : (
                <StepBody
                  stepKey={currentKey}
                  answers={answers}
                  update={update}
                  updateFollowup={updateFollowup}
                  emailLocked={emailLocked}
                  onUnlockEmail={() => setEmailLocked(false)}
                />
              )}
            </motion.div>
          </AnimatePresence>

          {error && <p className="text-red-600 text-sm font-sans mt-4 text-center">{error}</p>}

          {!isWelcome && (
            <div className="flex items-center justify-between mt-10">
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0 || submitting}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-30 font-sans"
              >
                <ArrowLeft size={16} /> back
              </button>
              <span className="text-xs text-muted-foreground font-sans">
                {step + 1} of {steps.length}
              </span>
              <button
                type="button"
                onClick={handleNext}
                disabled={!canNext() || submitting}
                className="flex items-center gap-1.5 bg-primary text-primary-foreground px-6 py-3 rounded-full font-sans text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
              >
                {submitting ? "sending..." : step === steps.length - 1 ? "submit" : "next"}
                {!submitting && <ArrowRight size={16} />}
              </button>
            </div>
          )}
        </div>
      </div>


      {/* Footer */}
      <footer className="px-6 py-8 border-t border-border bg-background">
        <div className="max-w-xl mx-auto flex flex-col items-center gap-3 text-sm font-sans text-muted-foreground">
          <a href="https://instagram.com/qwip" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
            Follow us on Instagram
          </a>
          <a href="https://apps.apple.com/gb/app/qwip/id1234567890" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
            Download qwip on the App Store
          </a>
        </div>
      </footer>
    </div>
  );
}

function StepBody({
  stepKey,
  answers,
  update,
  updateFollowup,
  emailLocked,
  onUnlockEmail,
}: {
  stepKey: string;
  answers: Answers;
  update: (p: Partial<Answers>) => void;
  updateFollowup: (p: Record<string, unknown>) => void;
  emailLocked: boolean;
  onUnlockEmail: () => void;
}) {
  switch (stepKey) {
    case "email":
      return (
        <div className="text-center">
          <h2 className="font-funnel font-bold text-3xl text-foreground mb-6">what's your email?</h2>
          <input
            type="email"
            value={answers.email}
            onChange={(e) => update({ email: e.target.value })}
            placeholder="you@example.com"
            autoFocus
            className="w-full px-5 py-4 border border-border rounded-xl bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary font-sans text-center"
          />
        </div>
      );

    case "use_case":
      return (
        <div>
          <h2 className="font-funnel font-bold text-3xl text-foreground mb-6 text-center">
            what did you use our pod for?
          </h2>
          {emailLocked && (
            <p className="text-xs text-muted-foreground font-sans mb-6 text-center">
              giving feedback as <span className="text-foreground">{answers.email}</span>.{" "}
              <button onClick={onUnlockEmail} className="underline hover:text-foreground">
                not you?
              </button>
            </p>
          )}
          <div className="grid gap-2">
            {useCaseOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => update({ use_case: opt.value })}
                className={`w-full text-left px-5 py-4 rounded-xl border transition-all font-sans ${
                  answers.use_case === opt.value
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card border-border hover:border-primary/40"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {answers.use_case === "other" && (
            <input
              type="text"
              value={answers.use_case_other ?? ""}
              onChange={(e) => update({ use_case_other: e.target.value })}
              placeholder="tell us what..."
              className="w-full mt-4 px-5 py-3 border border-border rounded-xl bg-card focus:outline-none focus:ring-2 focus:ring-primary font-sans"
            />
          )}
        </div>
      );

    case "followup":
      return <FollowupStep useCase={answers.use_case!} followup={answers.followup ?? {}} update={updateFollowup} />;

    case "pod_quality": {
      const pq = answers.pod_quality ?? { sound: 0, cleanliness: 0, comfort: 0, lighting: 0, unlock: 0 };
      const items: { key: keyof typeof pq; label: string }[] = [
        { key: "sound", label: "sound isolation" },
        { key: "cleanliness", label: "cleanliness" },
        { key: "comfort", label: "seat & comfort" },
        { key: "lighting", label: "lighting" },
        { key: "unlock", label: "ease of unlocking" },
      ];
      return (
        <div>
          <h2 className="font-funnel font-bold text-3xl text-foreground mb-6 text-center">rate the pod itself</h2>
          <div className="space-y-5">
            {items.map((it) => (
              <div key={it.key} className="bg-card border border-border rounded-xl p-4">
                <div className="text-sm font-sans text-foreground mb-3 text-center">{it.label}</div>
                <Stars
                  value={pq[it.key]}
                  size={28}
                  onChange={(n) => update({ pod_quality: { ...pq, [it.key]: n } })}
                />
              </div>
            ))}
          </div>
        </div>
      );
    }

    case "booking_price":
      return (
        <div>
          <h2 className="font-funnel font-bold text-3xl text-foreground mb-6 text-center">booking & price</h2>


          <div className="bg-card border border-border rounded-xl p-4 mb-5">
            <div className="text-sm font-sans text-foreground mb-3 text-center">was booking easy?</div>
            <Stars value={answers.booking_easy} size={28} onChange={(n) => update({ booking_easy: n })} />
          </div>

          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-sm font-sans text-foreground mb-3 text-center">did the price feel fair?</div>
            <div className="flex flex-wrap gap-2 justify-center mb-3">
              {["too cheap", "fair", "too expensive", "depends"].map((p) => (
                <Pill key={p} active={answers.price_feel === p} onClick={() => update({ price_feel: p })}>
                  {p}
                </Pill>
              ))}
            </div>
            {answers.price_feel === "depends" && (
              <input
                type="text"
                value={answers.price_note ?? ""}
                onChange={(e) => update({ price_note: e.target.value })}
                placeholder="depends on... (optional)"
                className="w-full mt-2 px-4 py-2.5 border border-border rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-primary font-sans text-sm"
              />
            )}
          </div>
        </div>
      );

    case "again":
      return (
        <div>
          <h2 className="font-funnel font-bold text-3xl text-foreground mb-6 text-center">would you book again?</h2>
          <div className="flex gap-3 justify-center mb-5">
            {(["yes", "maybe", "no"] as const).map((v) => (
              <Pill key={v} active={answers.would_book_again === v} onClick={() => update({ would_book_again: v })}>
                {v}
              </Pill>
            ))}
          </div>
          <textarea
            value={answers.would_book_again_why ?? ""}
            onChange={(e) => update({ would_book_again_why: e.target.value })}
            placeholder="why? (optional)"
            rows={3}
            className="w-full px-4 py-3 border border-border rounded-xl bg-card focus:outline-none focus:ring-2 focus:ring-primary font-sans text-sm"
          />
        </div>
      );

    case "one_change":
      return (
        <div>
          <h2 className="font-funnel font-bold text-3xl text-foreground mb-6 text-center">
            one thing we should change or add?
          </h2>
          <textarea
            value={answers.one_change ?? ""}
            onChange={(e) => update({ one_change: e.target.value })}
            placeholder="if there's one thing..."
            rows={5}
            autoFocus
            className="w-full px-5 py-4 border border-border rounded-xl bg-card focus:outline-none focus:ring-2 focus:ring-primary font-sans"
          />
        </div>
      );

    default:
      return null;
  }
}

function FollowupStep({
  useCase,
  followup,
  update,
}: {
  useCase: UseCase;
  followup: Record<string, unknown>;
  update: (p: Record<string, unknown>) => void;
}) {
  const StarBlock = ({ label, field }: { label: string; field: string }) => (
    <div className="bg-card border border-border rounded-xl p-4 mb-4">
      <div className="text-sm font-sans text-foreground mb-3 text-center">{label}</div>
      <Stars value={followup[field] as number} size={28} onChange={(n) => update({ [field]: n })} />
    </div>
  );

  const TextBlock = ({ label, field }: { label: string; field: string }) => (
    <textarea
      value={(followup[field] as string) ?? ""}
      onChange={(e) => update({ [field]: e.target.value })}
      placeholder={label}
      rows={3}
      className="w-full px-4 py-3 border border-border rounded-xl bg-card focus:outline-none focus:ring-2 focus:ring-primary font-sans text-sm"
    />
  );

  const distractionChips = ["outside noise", "seat comfort", "lighting", "temperature", "wifi", "other"];

  const heading = (text: string) => (
    <h2 className="font-funnel font-bold text-3xl text-foreground mb-6 text-center">{text}</h2>
  );
  const sub = (_text: string) => null;

  switch (useCase) {
    case "call":
      return (
        <div>
          {heading("about your call")}
          {sub("audio matters most here.")}
          <StarBlock label="audio quality (in & out)" field="audio_quality" />
          <TextBlock label="any echo, mic, or background issues? (optional)" field="audio_notes" />
        </div>
      );
    case "work":
    case "study": {
      const selected = (followup.distractions as string[]) ?? [];
      return (
        <div>
          {heading(useCase === "work" ? "about your work session" : "about your study session")}
          {sub("focus is the whole game.")}
          <StarBlock label="how focused could you be?" field="focus_rating" />
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-sm font-sans text-foreground mb-3 text-center">anything break your focus?</div>
            <div className="flex flex-wrap gap-2 justify-center">
              {distractionChips.map((c) => (
                <Pill
                  key={c}
                  active={selected.includes(c)}
                  onClick={() => {
                    const next = selected.includes(c) ? selected.filter((x) => x !== c) : [...selected, c];
                    update({ distractions: next });
                  }}
                >
                  {c}
                </Pill>
              ))}
            </div>
          </div>
        </div>
      );
    }
    case "break":
      return (
        <div>
          {heading("about your break")}
          {sub("did it do the job?")}
          <StarBlock label="did it help you reset?" field="reset_rating" />
          <TextBlock label="what would've made it better? (optional)" field="break_notes" />
        </div>
      );
    case "interview":
      return (
        <div>
          {heading("about your interview")}
          {sub("first impressions count.")}
          <StarBlock label="did it feel private & professional?" field="interview_rating" />
          <TextBlock label="anything that hurt the impression? (optional)" field="interview_notes" />
        </div>
      );
    case "recording":
      return (
        <div>
          {heading("about your recording")}
          {sub("we want it studio-grade.")}
          <StarBlock label="audio quality" field="rec_audio" />
          <div className="bg-card border border-border rounded-xl p-4 mb-4">
            <div className="text-sm font-sans text-foreground mb-3 text-center">was lighting good enough?</div>
            <div className="flex gap-2 justify-center">
              {["yes", "no", "didn't matter"].map((v) => (
                <Pill key={v} active={followup.lighting_ok === v} onClick={() => update({ lighting_ok: v })}>
                  {v}
                </Pill>
              ))}
            </div>
          </div>
          <TextBlock label="any other notes? (optional)" field="rec_notes" />
        </div>
      );
    default:
      return null;
  }
}
